/**
 * react-select grouped options for the wizard's product dropdown.
 *
 * Managed products appear first under a label that says what picking one
 * does, so a 30-second reach into the customer's ServiceNow is never a
 * surprise. Options carry managedProductId; the wizard uses its presence to
 * decide between the scan flow and the existing manual flow.
 *
 * `discovered` is the fallback for tenants with no managed product configured
 * for this vendor yet: products ServiceNow's CMDB already shows installed,
 * found via a discovery scan rather than a saved managed product. Its options
 * carry discoveredProductName (not managedProductId) so the wizard can send
 * them down the {vendorId, productName} path instead of {managedProductId}.
 */
/**
 * Whether a vendor can run a live inventory scan.
 *
 * `inventoryVendors` is the backend's list (GET /bsa/inventory/vendors, the
 * adapter registry). null/undefined means "not loaded or unavailable", which
 * answers false on purpose: offering a scan that cannot run is the failure
 * this exists to prevent, and the manual "All products" flow is always there.
 * No vendor name lives here — the list is data (STRY0038603).
 */
export function scanCapable(vendorId, inventoryVendors) {
  if (!vendorId || !Array.isArray(inventoryVendors)) return false;
  return inventoryVendors.includes(vendorId);
}

/**
 * `canScan` false hides the inventory AND discovered groups entirely (the
 * vendor has no adapter, or the list has not loaded): managed products still
 * exist for the vendor, but picking one would promise a scan we cannot
 * deliver — and discovery itself runs through the same adapter.
 */
export function buildProductGroups(managedProducts, productOptions, canScan = true, discovered) {
  const enabledManaged = (canScan ? managedProducts || [] : []).filter(mp => !mp.isDisabled);
  const inventory = enabledManaged.map(mp => ({
    value: `mp:${mp.id}`,
    label: mp.name,
    managedProductId: mp.id
  }));

  // Scenario 3 (Prevent + Plan for the same vendor): discovery is a *fallback*,
  // not a parallel list. A product that already has a managed product is
  // matched by fetch()'s CMDB filter on that same name, so surfacing it again
  // under "Discovered" would let a user create a second, managedProduct-less
  // project for one physical fleet. Drop discovered names that a managed
  // product already covers (case-insensitive, trimmed) so Discovered only ever
  // fills gaps Prevent hasn't configured.
  const managedNames = new Set(enabledManaged.map(mp => (mp.name || '').trim().toLowerCase()).filter(Boolean));
  const discoveredGaps = (canScan ? discovered || [] : []).filter(
    d => !managedNames.has((d.productName || '').trim().toLowerCase())
  );

  const groups = [];
  if (inventory.length) {
    groups.push({ label: 'Your inventory · scans ServiceNow for live versions', options: inventory });
  }
  if (discoveredGaps.length) {
    groups.push({
      label: 'Discovered in ServiceNow · scans your inventory',
      options: discoveredGaps.map(d => ({
        value: `disc:${d.productName}`,
        label: d.productName,
        discoveredProductName: d.productName,
        // The ODD product id to analyze (may differ from the CMDB display name,
        // e.g. F5's "F5 BIG-IP i7600" display vs. "i7600" ODD id).
        discoveredProductId: d.productId
      }))
    });
  }
  groups.push({ label: 'All products', options: productOptions || [] });
  return groups;
}
